/**
 * Cards selecionáveis de modo (Remoto / Presencial / Chat).
 * Inspirado no AttendanceTypeSelector, em versão compacta para o stub.
 */
import {
  Box,
  Card,
  CardActionArea,
  CardContent,
  Stack,
  Typography,
} from "@mui/material";
import { Chat, LocationOn, Videocam } from "@mui/icons-material";
import {
  PRONTO_ATENDIMENTO_MODE_LABELS,
  type ProntoAtendimentoMode,
} from "./types";

const MODE_OPTIONS: Array<{
  mode: ProntoAtendimentoMode;
  description: string;
  icon: React.ReactNode;
  colorKey: string;
  bgKey: string;
}> = [
  {
    mode: "presencial",
    description: "No consultório ou clínica",
    icon: <LocationOn sx={{ fontSize: 28 }} />,
    colorKey: "primary700.main",
    bgKey: "primary100.main",
  },
  {
    mode: "remoto",
    description: "Consulta por vídeo",
    icon: <Videocam sx={{ fontSize: 28 }} />,
    colorKey: "secondary700.main",
    bgKey: "secondary100.main",
  },
  {
    mode: "chat",
    description: "Atendimento por mensagens",
    icon: <Chat sx={{ fontSize: 28 }} />,
    colorKey: "error700.main",
    bgKey: "error100.main",
  },
];

interface ProntoAtendimentoModeCardsProps {
  value: ProntoAtendimentoMode | "";
  onChange: (mode: ProntoAtendimentoMode) => void;
}

export function ProntoAtendimentoModeCards({
  value,
  onChange,
}: ProntoAtendimentoModeCardsProps) {
  return (
    <Box
      display="grid"
      gridTemplateColumns={{ xs: "1fr", sm: "repeat(3, 1fr)" }}
      gap={1.5}
    >
      {MODE_OPTIONS.map((option) => {
        const selected = value === option.mode;
        return (
          <Card
            key={option.mode}
            elevation={0}
            sx={{
              borderRadius: 2,
              border: "1.5px solid",
              borderColor: selected ? option.colorKey : "divider",
              bgcolor: selected ? option.bgKey : "background.paper",
              transition: "border-color 0.15s ease, background-color 0.15s ease",
            }}
          >
            <CardActionArea
              onClick={() => onChange(option.mode)}
              sx={{ height: "100%", borderRadius: 2 }}
            >
              <CardContent sx={{ py: 1.5, px: 1.5 }}>
                <Stack spacing={1} alignItems="center" textAlign="center">
                  <Box
                    sx={{
                      backgroundColor: option.bgKey,
                      borderRadius: "50%",
                      p: 1,
                      color: option.colorKey,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                    }}
                  >
                    {option.icon}
                  </Box>
                  <Typography variant="subtitle2" fontWeight={700}>
                    {PRONTO_ATENDIMENTO_MODE_LABELS[option.mode]}
                  </Typography>
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    lineHeight={1.3}
                  >
                    {option.description}
                  </Typography>
                </Stack>
              </CardContent>
            </CardActionArea>
          </Card>
        );
      })}
    </Box>
  );
}
